import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BodyOf } from './client';
import { useApi, useMe } from './hooks';

/** React Query hooks for the improvement loop ("Learning"). */

export const learningKeys = {
  all: ['learning'] as const,
  overview: ['learning', 'overview'] as const,
  reports: ['learning', 'reports'] as const,
  report: (id: string) => ['learning', 'report', id] as const,
  decisions: ['learning', 'decisions'] as const,
  experiments: ['learning', 'experiments'] as const,
  playbook: ['learning', 'playbook'] as const,
  errors: (days: number) => ['learning', 'errors', days] as const,
  steps: (days: number) => ['learning', 'steps', days] as const,
  review: ['learning', 'review'] as const,
};

/** Only BeatSquares staff see the Learning area. */
export function useIsStaff(): boolean | undefined {
  const me = useMe();
  return me.data ? me.data.staff === true : undefined;
}

/**
 * Deciding, stopping and rolling back: staff with an admin membership. The web does not know the staff
 * org id; the API enforces the exact rule (admin in the staff org).
 */
export function useIsAdmin(): boolean {
  const me = useMe();
  return !!me.data?.staff && me.data.memberships.some((m) => m.role === 'admin');
}

export function useLearningOverview() {
  const api = useApi();
  return useQuery({ queryKey: learningKeys.overview, queryFn: () => api.call('learningOverview', {}) });
}

export function useReports() {
  const api = useApi();
  return useQuery({ queryKey: learningKeys.reports, queryFn: async () => (await api.call('listReports', {})).reports });
}

export function useReport(reportId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: learningKeys.report(reportId ?? ''),
    queryFn: async () => (await api.call('getReport', { reportId: reportId! })).report,
    enabled: !!reportId,
  });
}

export function useDecisions() {
  const api = useApi();
  return useQuery({
    queryKey: learningKeys.decisions,
    queryFn: async () => (await api.call('listDecisions', {})).decisions,
  });
}

export function useExperiments() {
  const api = useApi();
  return useQuery({
    queryKey: learningKeys.experiments,
    queryFn: async () => (await api.call('listExperiments', {})).experiments,
  });
}

export function usePlaybook() {
  const api = useApi();
  return useQuery({
    queryKey: learningKeys.playbook,
    queryFn: async () => (await api.call('listPlaybook', {})).versions,
  });
}

/** Every learning action can change versions, experiments and decisions: reload the whole area. */
function useInvalidateLearning() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: learningKeys.all });
}

export function useDecide() {
  const api = useApi();
  const invalidate = useInvalidateLearning();
  return useMutation({
    mutationFn: ({ decisionId, ...body }: BodyOf<'decide'> & { decisionId: string }) =>
      api.call('decide', { decisionId }, body),
    onSettled: invalidate,
  });
}

export function useStopExperiment() {
  const api = useApi();
  const invalidate = useInvalidateLearning();
  return useMutation({
    mutationFn: (experimentId: string) => api.call('stopExperiment', { experimentId }),
    onSettled: invalidate,
  });
}

export function useRollback() {
  const api = useApi();
  const invalidate = useInvalidateLearning();
  return useMutation({
    mutationFn: (body: BodyOf<'rollbackPlaybook'>) => api.call('rollbackPlaybook', {}, body),
    onSettled: invalidate,
  });
}

export function useErrorSheet(days: number) {
  const api = useApi();
  return useQuery({
    queryKey: learningKeys.errors(days),
    queryFn: () => api.call('errorSheet', { query: { days } }),
    placeholderData: (prev) => prev,
  });
}

export function useStepStats(days: number) {
  const api = useApi();
  return useQuery({
    queryKey: learningKeys.steps(days),
    queryFn: async () => (await api.call('stepStats', { query: { days } })).rows,
    placeholderData: (prev) => prev,
  });
}

export function useReviewQueue() {
  const api = useApi();
  return useQuery({ queryKey: learningKeys.review, queryFn: async () => (await api.call('reviewQueue', {})).items });
}

/** Saves a person's verdict on one step; the error sheet changes with it. */
export function useLabelStep() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ reelId, ...body }: BodyOf<'labelStep'> & { reelId: string }) =>
      api.call('labelStep', { reelId }, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['learning', 'errors'] });
    },
  });
}
